import { useState, useEffect } from "react";
import { CreditCard, Smartphone, Crown, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { getCountryConfig, detectCountryCode, type CountryPaymentConfig } from "@/data/currencyConfig";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";

const STRIPE_PRICES = {
  monthly: "price_1TIct6Ar5pEedHbO4HT1FjZg",
  yearly: "price_1TIctVAr5pEedHbOcpc0Is23",
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const PaymentMethodModal = ({ open, onOpenChange }: Props) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [config, setConfig] = useState<CountryPaymentConfig | null>(null);
  const [loading, setLoading] = useState(false);
  const [billingCycle, setBillingCycle] = useState<"monthly" | "yearly">("monthly");

  useEffect(() => {
    if (!open) return;
    const init = async () => {
      let countryCode = detectCountryCode();
      if (user) {
        const { data } = await supabase.from("profiles").select("country").eq("id", user.id).single();
        if (data?.country) countryCode = data.country;
      }
      setConfig(getCountryConfig(countryCode));
    };
    init();
  }, [user, open]);

  const handleStripeCheckout = async () => {
    if (!user) {
      toast({ title: "Connexion requise", description: "Connectez-vous pour souscrire.", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: { priceId: STRIPE_PRICES[billingCycle] },
      });
      if (error) throw error;
      if (data?.url) window.open(data.url, "_blank");
      onOpenChange(false);
    } catch (e: any) {
      toast({ title: "Erreur", description: e.message || "Impossible de créer la session.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleMobileMoney = () => {
    onOpenChange(false);
    navigate("/tarifs?method=mobile_money");
  };

  const paymentMethods = config?.paymentMethods || [];
  const hasCard = paymentMethods.some((m) => m.id === "card");
  const hasMomo = paymentMethods.some((m) => m.id.includes("money") || m.id.includes("mtn") || m.id.includes("airtel") || m.id.includes("orange"));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md rounded-3xl border-2 border-border">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg font-black">
            <Crown className="w-5 h-5 text-primary" /> Choisir un mode de paiement
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* Billing cycle toggle */}
          {hasCard && (
            <div className="flex items-center gap-2 p-1 rounded-2xl bg-muted w-fit mx-auto">
              <button
                onClick={() => setBillingCycle("monthly")}
                className={`px-4 py-2 rounded-xl text-sm font-bold transition-colors ${billingCycle === "monthly" ? "bg-primary text-primary-foreground shadow" : "text-muted-foreground"}`}
              >
                Mensuel
              </button>
              <button
                onClick={() => setBillingCycle("yearly")}
                className={`px-4 py-2 rounded-xl text-sm font-bold transition-colors ${billingCycle === "yearly" ? "bg-primary text-primary-foreground shadow" : "text-muted-foreground"}`}
              >
                Annuel <span className="text-xs opacity-80">-20%</span>
              </button>
            </div>
          )}

          {/* Card payment */}
          {hasCard && (
            <button
              onClick={handleStripeCheckout}
              disabled={loading}
              className="w-full flex items-center gap-4 p-4 rounded-2xl border-2 border-border hover:border-primary hover:bg-primary/5 transition-all text-left"
            >
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center bg-primary/10">
                {loading ? <Loader2 className="w-6 h-6 text-primary animate-spin" /> : <CreditCard className="w-6 h-6 text-primary" />}
              </div>
              <div className="flex-1">
                <p className="font-bold text-foreground text-sm">💳 Carte bancaire</p>
                <p className="text-xs text-muted-foreground">Visa, Mastercard — Paiement sécurisé via Stripe</p>
              </div>
            </button>
          )}

          {/* Mobile Money */}
          {hasMomo && (
            <button
              onClick={handleMobileMoney}
              className="w-full flex items-center gap-4 p-4 rounded-2xl border-2 border-border hover:border-secondary hover:bg-secondary/5 transition-all text-left"
            >
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center bg-secondary/10">
                <Smartphone className="w-6 h-6 text-secondary" />
              </div>
              <div className="flex-1">
                <p className="font-bold text-foreground text-sm">📱 Mobile Money</p>
                <p className="text-xs text-muted-foreground">MTN MoMo, Airtel Money</p>
              </div>
            </button>
          )}

          {/* Fallback if no methods detected */}
          {!hasCard && !hasMomo && (
            <button
              onClick={() => { onOpenChange(false); navigate("/tarifs"); }}
              className="w-full btn-hero py-3 text-sm"
            >
              Voir les options de paiement
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentMethodModal;
